import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { T, Icon, stitchDialogPaper, stitchGradientBtn, stitchOutlineBtn } from '../ProjectOverview/parts/stitch';

/**
 * Resultaat van een generator (markdown) met kopieer + bewaar-als-notitie —
 * gebruikt door de opstart-wizard; StagePanel heeft dezelfde acties inline.
 */
function GenResultDialog({ result, onClose, projectRecId }) {
  // "Sluiten" gooide het gegenereerde document weg zonder dat dat ergens stond —
  // uit de feedback: "als ik op sluiten druk, wordt het dan opgeslagen? Dit is me
  // onduidelijk." Nu zegt het venster wat er gebeurt, en na bewaren verandert de knop.
  const [bewaard, setBewaard] = useState(false);
  useEffect(() => { setBewaard(false); }, [result]);

  const bewaarAlsNotitie = async () => {
    if (!result?.markdown) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/project-notes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: projectRecId,
          title: (result.titel || 'Gegenereerd document').slice(0, 50),
          notes: result.markdown,
          source: 'Manual',
        }),
      });
      if (!r.ok) throw new Error('Opslaan mislukt');
      setBewaard(true);
      toast.success('Bewaard als projectnotitie — terug te vinden onder Notities bij het project');
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Dialog open={!!result} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: stitchDialogPaper }}>
      <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800 }}>{result?.titel || 'Gegenereerd document'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{
          fontFamily: T.fontBody, fontSize: '0.875rem', color: T.text, lineHeight: 1.7,
          '& p': { m: 0, mb: 1 },
          '& p:last-child': { mb: 0 },
          '& ul, & ol': { my: 0.5, pl: 2.5 },
          '& li': { mb: 0.4 },
          '& h1, & h2, & h3, & h4': {
            fontFamily: T.font, fontWeight: 800, color: T.text,
            fontSize: '1rem', mt: 2, mb: 0.75,
          },
          '& h1:first-of-type, & h2:first-of-type, & h3:first-of-type': { mt: 0 },
          '& strong': { fontWeight: 700 },
          '& hr': { border: 0, borderTop: `1px solid ${T.surfaceHigh}`, my: 1.5 },
        }}>
          <ReactMarkdown>{result?.markdown || ''}</ReactMarkdown>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: bewaard ? '#166534' : T.outline, flex: 1 }}>
          {bewaard
            ? '✓ Staat bij het project onder Notities.'
            : 'Dit venster bewaart niets vanzelf — sluiten gooit het document weg.'}
        </Typography>
        <Box component="span" sx={stitchOutlineBtn}
          onClick={() => { navigator.clipboard?.writeText(result?.markdown || ''); toast.success('Gekopieerd'); }}>
          <Icon name="content_copy" size={16} /> Kopieer
        </Box>
        {!bewaard && (
          <Box component="span" sx={stitchOutlineBtn} onClick={bewaarAlsNotitie}>
            <Icon name="note_add" size={16} /> Bewaar als notitie
          </Box>
        )}
        <Box component="span" onClick={onClose} sx={stitchGradientBtn}>
          {bewaard ? 'Sluiten' : 'Sluiten zonder bewaren'}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default GenResultDialog;
